import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { GraduationCap, Loader2, Award, Download, BookOpenCheck, Lightbulb } from "lucide-react";
import { api, apiError, API, downloadFile } from "../../lib/api";
import { ModuleCard } from "../../components/academia/ModuleCard";
import { Plan30 } from "../../components/academia/Plan30";
import { EvalFinal } from "../../components/academia/EvalFinal";

const BASE = "/academia-lumi";

export default function AcademiaLumi() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(BASE);
      setData(d);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-sky-400" size={30} /></div>;
  }

  const pct = data.progress_pct;
  const complete = pct >= 100;

  return (
    <div data-testid="academia-lumi-page" className="space-y-6 max-w-4xl">
      <div className="rounded-2xl border border-sky-400/40 p-6 sm:p-8" style={{ background: "linear-gradient(135deg, #0B1F3A, #123358)" }}>
        <div className="flex items-center gap-2 text-sky-300">
          <Lightbulb size={20} />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Capacitación exclusiva · Distribuidores LUMI</span>
        </div>
        <h1 className="font-bold text-3xl sm:text-4xl text-zinc-50 mt-2">Academia <span className="text-sky-400">LUMI</span></h1>
        <p className="text-sm text-sky-100/80 mt-2">{data.intro.subtitle} · Tutoría educativa para niños de 6 a 14 años.</p>

        <div className="mt-4 rounded-xl bg-black/25 border border-white/10 p-4">
          <p className="text-xs font-bold text-sky-300 uppercase tracking-wider mb-1">Objetivo</p>
          <p className="text-sm text-zinc-200">{data.intro.objetivo}</p>
          <p className="text-xs font-bold text-sky-300 uppercase tracking-wider mt-3 mb-1">Cómo se usa (muy fácil)</p>
          <ol className="space-y-1">
            {data.intro.como_usar.map((c, i) => (
              <li key={c} className="text-sm text-zinc-300 flex gap-2">
                <span className="text-sky-400 font-black">{i + 1}.</span> {c}
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-zinc-300 font-semibold">Tu progreso</span>
            <span data-testid="lumi-progress-pct" className="text-sky-300 font-black">{pct}%</span>
          </div>
          <div className="h-3 rounded-full bg-black/40 border border-white/10 overflow-hidden">
            <div data-testid="lumi-progress-bar" className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-300 transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[11px] text-zinc-400 mt-1.5">
            {data.completed_exercises}/7 ejercicios · {data.completed_plan}/6 etapas del plan · evaluación {data.eval_done ? "✓" : "pendiente"}
          </p>
        </div>

        {complete && (
          <div data-testid="lumi-complete-banner" className="mt-5 rounded-xl border border-sky-400/60 bg-sky-400/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <Award size={28} className="text-sky-300 shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-sky-200">¡Felicidades! Completaste la Academia LUMI</p>
              <p className="text-xs text-zinc-300">Descarga tu constancia con tu nombre.</p>
            </div>
            <button
              data-testid="lumi-certificate-btn"
              onClick={() => downloadFile(`${API}${BASE}/constancia`, "constancia-academia-lumi.pdf")}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-black bg-sky-400 text-[#0B1F3A] hover:bg-sky-300 shrink-0"
            >
              <Download size={14} /> Descargar constancia
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <BookOpenCheck size={18} className="text-sky-400" />
        <h2 className="font-bold text-xl text-zinc-100">Los 7 módulos</h2>
      </div>
      <div className="space-y-3">
        {data.modules.map((m, i) => (
          <ModuleCard key={m.module_id} module={m} index={i} onSaved={setData} basePath={BASE} />
        ))}
      </div>

      <Plan30 steps={data.plan30} onSaved={setData} basePath={BASE} />
      <EvalFinal evalData={{ questions: data.eval_questions, eval_done: data.eval_done }} onSaved={setData} basePath={BASE} />

      <p className="text-[11px] text-zinc-500 pb-4">
        <GraduationCap size={12} className="inline mr-1" />
        Academia LUMI · Este manual no constituye un plan de compensaciones ni una promesa de ingresos.
      </p>
    </div>
  );
}
